import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Alert,
    LinearProgress,
    Chip,
    CircularProgress
} from '@mui/material';
import { Question } from '../../types/course';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface Correction {
    questionIndex: number;
    questionText: string;
    userAnswer: any;
    isCorrect: boolean;
    correctAnswer: string | null;
    explanation: string | null;
    points: number;
}

interface QuizResult {
    score: number;
    corrections: Correction[];
    passed: boolean;
}

interface QuizPlayerProps {
    questions: Question[];
    onComplete: (answers: any[]) => Promise<QuizResult | null>;
}

const QuizPlayer: React.FC<QuizPlayerProps> = ({ questions, onComplete }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<any[]>(new Array(questions.length).fill(null));
    const [showResults, setShowResults] = useState(false);
    const [score, setScore] = useState(0);
    const [corrections, setCorrections] = useState<Correction[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shuffledQuestions, setShuffledQuestions] = useState<any[]>([]);

    // Helper to shuffle array
    const shuffleArray = (array: any[]) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    useEffect(() => {
        // Initialize shuffled states for questions that need it
        if (questions && questions.length > 0) {
            const initializedState = questions.map(q => {
                if (q.type === 'order') {
                    // Start with a shuffled version of the items
                    return shuffleArray(q.orderItems || []);
                } else if (q.type === 'match_text') {
                    const pairs = q.pairs || [];
                    // 1. Shuffle the list of pairs (rows)
                    // 2. Clear the 'right' side (user answer) initially
                    const shuffledPairs = shuffleArray(pairs.map((p: any) => ({ ...p, right: '' })));

                    // 3. Prepare the list of all possible answers (for the dropdown), shuffled
                    const allRights = [...new Set(pairs.map((p: any) => p.right))];
                    const rightOptions = shuffleArray(allRights);

                    return {
                        pairs: shuffledPairs,
                        rightOptions: rightOptions
                    };
                }
                return null;
            });
            setShuffledQuestions(initializedState);
        }
    }, [questions]);

    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    const handleAnswer = (answer: any) => {
        const newAnswers = [...answers];
        newAnswers[currentQuestionIndex] = answer;
        setAnswers(newAnswers);
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // Submit to backend for grading
            submitQuiz();
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const submitQuiz = async () => {
        setIsSubmitting(true);
        const result = await onComplete(answers);
        setIsSubmitting(false);

        if (result) {
            setScore(result.score);
            setCorrections(result.corrections);
            setShowResults(true);
        }
    };

    const handleRetry = () => {
        setCurrentQuestionIndex(0);
        setAnswers(new Array(questions.length).fill(null));
        setShowResults(false);
        setScore(0);
        setCorrections([]);
    };

    const checkAnswer = (question: Question, answer: any): boolean => {
        if (!answer) return false;

        switch (question.type) {
            case 'qcm':
                if (question.multiSelect) {
                    const correct = question.correctOptions || [];
                    return JSON.stringify(answer.sort()) === JSON.stringify(correct.sort());
                } else {
                    const correctIndex = question.correctOptions?.[0] ?? question.correctOptionIndex;
                    return answer === correctIndex;
                }

            case 'true_false':
                return answer === question.correctAnswer;

            case 'fill_blank':
                return answer.toLowerCase().trim() === (question.correctAnswer as string).toLowerCase().trim();

            case 'order':
                return JSON.stringify(answer) === JSON.stringify(question.orderItems);

            case 'match_text':
                // Check if all pairs are correctly matched (order independent)
                const correctPairs = question.pairs || [];
                if (!Array.isArray(answer) || answer.length !== correctPairs.length) return false;

                return answer.every((ansPair: any) => {
                    const correspondingCorrect = correctPairs.find((p: any) => p.left === ansPair.left);
                    return correspondingCorrect && correspondingCorrect.right === ansPair.right;
                });

            case 'code':
                // Simple check: contains solution keywords
                const solution = question.codeSolution || '';
                return answer.includes(solution);

            default:
                return false;
        }
    };

    const renderQuestion = () => {
        const answer = answers[currentQuestionIndex];

        switch (currentQuestion.type) {
            case 'qcm':
                return (
                    <Box>
                        {currentQuestion.multiSelect ? (
                            <Box>
                                {currentQuestion.options?.map((option: string, idx: number) => (
                                    <FormControlLabel
                                        key={idx}
                                        control={
                                            <Checkbox
                                                checked={answer?.includes(idx) || false}
                                                onChange={(e) => {
                                                    const newAnswer = answer || [];
                                                    if (e.target.checked) {
                                                        handleAnswer([...newAnswer, idx]);
                                                    } else {
                                                        handleAnswer(newAnswer.filter((i: number) => i !== idx));
                                                    }
                                                }}
                                            />
                                        }
                                        label={option}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <RadioGroup
                                value={answer ?? ''}
                                onChange={(e) => handleAnswer(parseInt(e.target.value))}
                            >
                                {currentQuestion.options?.map((option: string, idx: number) => (
                                    <FormControlLabel
                                        key={idx}
                                        value={idx}
                                        control={<Radio />}
                                        label={option}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    </Box>
                );

            case 'true_false':
                return (
                    <RadioGroup
                        value={answer === undefined ? '' : answer.toString()}
                        onChange={(e) => handleAnswer(e.target.value === 'true')}
                    >
                        <FormControlLabel value="true" control={<Radio />} label="Vrai" />
                        <FormControlLabel value="false" control={<Radio />} label="Faux" />
                    </RadioGroup>
                );

            case 'fill_blank':
                return (
                    <TextField
                        fullWidth
                        value={answer || ''}
                        onChange={(e) => handleAnswer(e.target.value)}
                        placeholder="Votre réponse..."
                        sx={{ fontFamily: 'Quicksand, sans-serif' }}
                    />
                );

            case 'order':
                const items = answer || shuffledQuestions[currentQuestionIndex] || [];
                // If initializing and not yet shuffled, don't show anything to prevent spoiler
                if (!answer && !shuffledQuestions[currentQuestionIndex]) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;

                return (
                    <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            Glissez-déposez pour réorganiser (ou utilisez les boutons)
                        </Typography>
                        {items.map((item: string, idx: number) => (
                            <Paper key={idx} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ flex: 1 }}>{item}</Typography>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        if (idx > 0) {
                                            const newItems = [...items];
                                            [newItems[idx], newItems[idx - 1]] = [newItems[idx - 1], newItems[idx]];
                                            handleAnswer(newItems);
                                        }
                                    }}
                                    disabled={idx === 0}
                                >
                                    ↑
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        if (idx < items.length - 1) {
                                            const newItems = [...items];
                                            [newItems[idx], newItems[idx + 1]] = [newItems[idx + 1], newItems[idx]];
                                            handleAnswer(newItems);
                                        }
                                    }}
                                    disabled={idx === items.length - 1}
                                >
                                    ↓
                                </Button>
                            </Paper>
                        ))}
                    </Box>
                );

            case 'match_text':
                const pairs = answer || shuffledQuestions[currentQuestionIndex]?.pairs || [];
                const rights = shuffledQuestions[currentQuestionIndex]?.rightOptions || [];

                // If initializing and not yet shuffled, don't show anything to prevent spoiler
                if (!answer && !shuffledQuestions[currentQuestionIndex]) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;

                return (
                    <Box>
                        {pairs.map((pair: any, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                <Typography sx={{ flex: 1 }}>{pair.left}</Typography>
                                <Typography>→</Typography>
                                <TextField
                                    select
                                    value={(pair.right as string) || ''}
                                    onChange={(e) => {
                                        const newPairs = [...pairs];
                                        newPairs[idx].right = e.target.value;
                                        handleAnswer(newPairs);
                                    }}
                                    SelectProps={{ native: true }}
                                    sx={{ flex: 1 }}
                                >
                                    <option value="">Sélectionnez...</option>
                                    {rights.map((right: string, ridx: number) => (
                                        <option key={ridx} value={right}>{right}</option>
                                    ))}
                                </TextField>
                            </Box>
                        ))}
                    </Box>
                );

            case 'code':
                return (
                    <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            Langage: {currentQuestion.codeLanguage}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={10}
                            value={answer || currentQuestion.codeSnippet || ''}
                            onChange={(e) => handleAnswer(e.target.value)}
                            sx={{ fontFamily: 'monospace', mt: 1 }}
                        />
                    </Box>
                );

            default:
                return <Typography color="error">Type de question non supporté</Typography>;
        }
    };

    const renderResults = () => {
        const scoreColor = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
        const scoreIcon = score >= 70 ? '🎉' : score >= 50 ? '💪' : '📚';

        return (
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {/* Score Header */}
                <Paper sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${scoreColor} 0%, ${scoreColor}dd 100%)`,
                    color: 'white',
                    textAlign: 'center',
                    boxShadow: `0 8px 32px ${scoreColor}40`
                }}>
                    <Typography variant="h2" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, mb: 1 }}>
                        {scoreIcon} {score}%
                    </Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 500, opacity: 0.95 }}>
                        {score >= 70 ? 'Excellent travail ! Vous avez réussi !' :
                            score >= 50 ? 'Pas mal, mais vous pouvez faire mieux.' :
                                'Continuez à vous entraîner, vous allez y arriver !'}
                    </Typography>
                </Paper>

                {/* Questions Review */}
                <Typography variant="h6" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, mb: 3, color: '#2d3748' }}>
                    Révision des questions
                </Typography>

                {corrections.map((correction, idx) => (
                    <Paper key={idx} sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: 3,
                        border: `2px solid ${correction.isCorrect ? '#10b981' : '#ef4444'}`,
                        bgcolor: correction.isCorrect ? '#f0fdf4' : '#fef2f2',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)'
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: correction.isCorrect ? '#10b981' : '#ef4444',
                                color: 'white'
                            }}>
                                {correction.isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, color: '#2d3748' }}>
                                    Question {idx + 1}
                                </Typography>
                            </Box>
                            <Chip
                                label={`${correction.points} pts`}
                                size="small"
                                sx={{
                                    bgcolor: correction.isCorrect ? '#10b981' : '#ef4444',
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        </Box>
                        <Typography variant="body1" sx={{ fontFamily: 'Quicksand, sans-serif', mb: 2, color: '#4a5568' }}>
                            {correction.questionText}
                        </Typography>

                        {/* Show correct answer when user is wrong */}
                        {!correction.isCorrect && correction.correctAnswer && (
                            <Alert
                                severity="success"
                                icon={<CheckCircleIcon />}
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    fontFamily: 'Quicksand, sans-serif',
                                    bgcolor: '#dcfce7',
                                    border: '1px solid #86efac'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
                                    ✅ Bonne réponse : {correction.correctAnswer}
                                </Typography>
                            </Alert>
                        )}

                        {/* Show explanation if available */}
                        {correction.explanation && (
                            <Alert
                                severity="info"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    fontFamily: 'Quicksand, sans-serif'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                    💡 {correction.explanation}
                                </Typography>
                            </Alert>
                        )}
                    </Paper>
                ))}

                {/* Retry Button for Failed Quizzes */}
                {score < 50 && (
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleRetry}
                            sx={{
                                fontFamily: 'Quicksand, sans-serif',
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 6,
                                py: 2,
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                                }
                            }}
                        >
                            🔄 Recommencer le quiz
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    if (showResults) {
        return renderResults();
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{
                mb: 4,
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700 }}>
                        Question {currentQuestionIndex + 1} / {questions.length}
                    </Typography>
                    <Chip
                        label={`${currentQuestion.points} points`}
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontFamily: 'Quicksand, sans-serif',
                            fontWeight: 600
                        }}
                    />
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: 'white',
                            borderRadius: 5
                        }
                    }}
                />
            </Box>

            {/* Question Card */}
            <Paper sx={{
                p: 4,
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #f0f0f0',
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                }
            }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        fontWeight: 600,
                        mb: 3,
                        color: '#2d3748'
                    }}
                >
                    {currentQuestion.questionText}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    {renderQuestion()}
                </Box>
            </Paper>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        borderWidth: 2,
                        '&:hover': {
                            borderWidth: 2
                        }
                    }}
                >
                    ← Précédent
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={answers[currentQuestionIndex] === null || answers[currentQuestionIndex] === undefined}
                    sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                        },
                        '&:disabled': {
                            background: '#e0e0e0'
                        }
                    }}
                >
                    {currentQuestionIndex === questions.length - 1 ? 'Terminer ✓' : 'Suivant →'}
                </Button>
            </Box>
        </Box>
    );
};

export default QuizPlayer;
